/**
 * Section close — warn, never block (spec §2.4, §4 rule 8).
 *
 * Two things happen when a surveyor leaves a section, and neither of them can
 * stop them leaving:
 *
 *  1. **The recommended shot list is shown with got/missing state.** If shots
 *     are missing the surveyor is told, in the same sentence, that they can
 *     close anyway and what the office will see. The pit may genuinely be
 *     locked out; a form that refuses to move on in that situation is a form
 *     that gets abandoned in a basement.
 *
 *  2. **The surveyor's flag — builders only.** Until D-033 the section screen
 *     let the surveyor flag the section as blocked in their own words, which
 *     became a `Gap` with `reason:'surveyor_flagged'` and a `surveyorNote`.
 *     D-033 R15 deleted that card: what stopped the surveyor is written in the
 *     section's notes, and no phone door has raised a flag since. The builders
 *     (`flaggedGapId`, `buildFlagGap`, `applyFlag`, `flaggedGapOf`) stay for
 *     the record TYPE — records sent before D-033 carry such gaps, the sealed
 *     face and the office read them, and the shape must go on parsing. Live
 *     results do not read them (round 8, task 1).
 *
 * Pure: documents and sections in, gaps and strings out. No React, no store, no
 * clock — the caller that owns the store persists what these functions build.
 */

import { photoCoversShot, qualifyTitle } from '../../core/sections';
import type { Gap, SectionInstance, SurveyDocument } from '../../core/types';
import {
  isSectionFinished,
  primaryBlocker,
  roomOf,
  sectionHeadline,
  type SectionBlocker,
  type SectionHeadline,
  type SurveyResults,
} from './results';

/* ────────────────────────────── the copy ────────────────────────────────── */

/* ─────────────────────────── the surveyor's flag ────────────────────────── */

/**
 * A section carries at most ONE surveyor flag, so its id is derived from the
 * section rather than minted per tap. Editing the note therefore updates the
 * same gap row instead of appending a second one, on this device and on the
 * server, and re-flagging after a reload is idempotent.
 *
 * The shape matches `gapId()` in src/core/validation.ts — `section#reason#key` —
 * so a flagged gap sorts and reads beside the engine's own.
 */
export function flaggedGapId(sectionInstanceId: string): string {
  return `${sectionInstanceId}#surveyor_flagged#section`;
}

/**
 * The room-qualified title. A gap travels away from the screen that gave it
 * context — into a Telegram message Chip reads in a truck — so "Pit · HW 1" on
 * its own is not enough.
 */
export function qualifiedTitle(doc: SurveyDocument, section: SectionInstance): string {
  const room = roomOf(doc, section.scope);
  return room ? qualifyTitle(room.name, section.title) : section.title;
}

/** Terminate the surveyor's own words without doubling their punctuation. */
function sentence(text: string): string {
  return /[.!?]$/.test(text) ? text : `${text}.`;
}

/**
 * Build the gap for a blocked section.
 *
 * `message` is self-contained because it is what the notification renders;
 * `surveyorNote` repeats the reason on its own line there (see `renderGap` in
 * src/core/notify.ts). A flag with no note is still a legitimate flag — the
 * surveyor may be holding a flashlight in the other hand — so an empty note
 * produces a plain, honest message rather than being refused.
 */
export function buildFlagGap(
  doc: SurveyDocument,
  section: SectionInstance,
  note: string,
): Gap {
  const title = qualifiedTitle(doc, section);
  const reason = note.trim();
  return {
    id: flaggedGapId(section.id),
    sectionInstanceId: section.id,
    sectionTitle: title,
    reason: 'surveyor_flagged',
    message: reason
      ? `${title} is incomplete — ${sentence(reason)}`
      : `${title} is incomplete — the surveyor flagged it as blocked.`,
    ...(reason ? { surveyorNote: reason } : {}),
  };
}

/**
 * The flag already on this section, if the surveyor has raised one.
 *
 * Matched on the section and the reason, not only on the derived id: a flag
 * that was raised on another device, or minted by the server, keeps its own id,
 * and a section that reads "not flagged" because the id did not match would
 * invite a second flag for the same locked pit.
 */
export function flaggedGapOf(doc: SurveyDocument, sectionInstanceId: string): Gap | undefined {
  const id = flaggedGapId(sectionInstanceId);
  return (
    doc.gaps.find((gap) => gap.id === id) ??
    doc.gaps.find(
      (gap) => gap.sectionInstanceId === sectionInstanceId && gap.reason === 'surveyor_flagged',
    )
  );
}

/**
 * Write the flag onto a document, in place — the mutator `setStructure` wants.
 *
 * One flag per section, always. An existing flag is REPLACED and keeps its own
 * id, so a note edited on a survey that arrived from the server updates that
 * gap rather than growing a second one beside it.
 */
export function applyFlag(document: SurveyDocument, gap: Gap): void {
  const existing = flaggedGapOf(document, gap.sectionInstanceId);
  if (!existing) {
    document.gaps.push(gap);
    return;
  }
  const at = document.gaps.indexOf(existing);
  document.gaps[at] = { ...gap, id: existing.id };
}

/* ────────────────────────────── the shot list ───────────────────────────── */

export interface ShotRow {
  shotListId: string;
  label: string;
  got: boolean;
}

export interface SectionCloseView {
  /** Prompted shots only — a conditional shot whose predicate is false is absent. */
  rows: ShotRow[];
  missingCount: number;
  flagged: Gap | undefined;
}

/**
 * Everything the close panel shows, decided by the engines.
 *
 * `missingShots()` (§4 rule 8) owns "missing"; a photo tagged with the shot's
 * id owns "got"; a shot that is neither was never prompted — there is no
 * condensate route on a chilled-water job — and is not listed at all rather
 * than listed and permanently unsatisfiable.
 */
export function sectionCloseView(
  doc: SurveyDocument,
  section: SectionInstance,
  missingShotIds: ReadonlySet<string>,
): SectionCloseView {
  const rows: ShotRow[] = [];
  for (const shot of section.shotList) {
    // The same definition of "taken" the record uses (`missingShots`) — one
    // photo, one answer, and a shot's former address counts (Amendment 6).
    const got = doc.photos.some((photo) => photoCoversShot(photo, section, shot));
    const missing = missingShotIds.has(shot.id);
    if (!got && !missing) continue;
    rows.push({ shotListId: shot.id, label: shot.label, got });
  }

  const missingCount = rows.filter((row) => !row.got).length;
  return {
    rows,
    missingCount,
    flagged: flaggedGapOf(doc, section.id),
  };
}

/* ═══════════════ why this section will not read "done" — D-021 ════════════ */

/**
 * The design authority, live on production, 2026-08-07:
 *
 *   *"It just will not let me close out the electrical section no matter what
 *    I try."*
 *
 * She could close it — closing has never been blocked and must never be (§2.4:
 * warn, never block; the pit may genuinely be locked out). What she could not
 * do was make it read DONE, and the panel she was standing on said nothing at
 * all about why. §4 rule 3 was failing over two questions still sitting on the
 * D-016 defaults, two cards further down the same screen.
 *
 * So the close panel now carries the reason, at the moment and place of refusal.
 * It carries THE SAME `SectionBlocker[]` the section header names and the hall
 * indicator lamps read — one list, read three times, so no two surfaces can
 * describe the same section differently.
 *
 * Nothing here decides anything. `isSectionFinished` is the one decision, and it
 * lives in results.ts with the engines that make it.
 */
export interface SectionCloseReason {
  /** The single decision (results.ts), never a second opinion assembled here. */
  finished: boolean;
  /** The one sentence at the top of the panel. Never a bare state label. */
  headline: SectionHeadline;
  /** Every reason it will not read done, in full, in the engine's own words. */
  blockers: readonly SectionBlocker[];
  /** The ONE obvious thing to do about it (D-014), or undefined when finished. */
  next?: SectionBlocker;
}

/*
 * `explainAtClose`, `spokenByBuilder` and `BUILDER_POINTER` served the
 * reason panel D-030 deleted (the close-now narration — her own example of
 * clutter). The walk guard
 * reads `sectionCloseReason` directly and says one line, once.
 */



export function sectionCloseReason(
  results: SurveyResults,
  sectionInstanceId: string,
): SectionCloseReason {
  const progress = results.progressBySection.get(sectionInstanceId);
  const finished = isSectionFinished(progress);
  const blockers = progress?.blockers ?? [];
  const next = primaryBlocker(progress);

  // The close-now narration retired with the reason panel (D-030) — the

  return {
    finished,
    headline: sectionHeadline(progress),
    blockers,
    ...(next ? { next } : {}),
  };
}
